import {GitLabBranch, GitLabPipelineDetails, GitLabProject} from "@/interfaces/gitlab";
import GitLab from "@/clients/gitlab";
import {Pipeline} from "@/interfaces";

class GitlabService {
    private gitlab: GitLab;
    private cache_protected_branches_per_project = new Map<number, GitLabBranch[]>()
    private cache_projects_failed = new Map<number, Date>()

    constructor(gitlab: GitLab) {
        this.gitlab = gitlab;
    }

    private retrieve_protected_branches(project: GitLabProject): Promise<GitLabBranch[]> {
        // console.log("init " + project.name_with_namespace)
        return this.gitlab
            .getBranches(project.id)
            .then(
                branches => {
                    const protected_branches: GitLabBranch[] = []
                    for (const branch of branches) {
                        // console.log("  branch " + branch.name + " protected " + branch.protected)
                        if (branch.protected) {
                            protected_branches.push(branch)
                        }
                    }
                    return Promise.resolve(protected_branches)
                },
                _ => {
                    console.log("project has no branches " + project.name)
                    return Promise.resolve([])
                }
            )
    }

    private isPipelineZombie(pipeline: GitLabPipelineDetails) {
        return !pipeline.started_at && pipeline.status !== 'pending'
    }

    private async retrieveProjectPipelineDetails(projects: GitLabProject[],
                                                 beta_show_only_protected_branches: boolean,
                                                 beta_show_scheduled_builds: boolean): Promise<Pipeline[]> {
        interface ProjectDetailsRequest {
            project: GitLabProject
            pipelineDetails_call: () => Promise<GitLabPipelineDetails>
            schedule_description?: string
            schedule_url?: string
        }

        const requests: ProjectDetailsRequest[] = []

        if (beta_show_only_protected_branches) {
            interface GitLabProjectRef {
                project: GitLabProject
                ref: string
            }

            const projects_with_ref: GitLabProjectRef[] = [];
            for (const project of projects) {
                let branches = this.cache_protected_branches_per_project.get(project.id);
                if (branches) {
                    // already retrieved
                } else {
                    branches = await new GitlabService(this.gitlab).retrieve_protected_branches(project)
                    this.cache_protected_branches_per_project.set(project.id, branches)
                }

                for (const branch of branches) {
                    projects_with_ref.push(
                        {
                            project: project,
                            ref: branch.name
                        }
                    )
                }
            }

            projects_with_ref.map((project_with_ref) => {
                const projectId = project_with_ref.project.id;
                const reference = project_with_ref.ref;
                requests.push({
                    pipelineDetails_call: () => this.gitlab.getLatestPipeline(projectId, reference),
                    project: project_with_ref.project,
                })
            })
        } else {
            projects.map((project) => {
                console.log(`fetch latest pipeline for ${project.name} ${project.id}`)
                requests.push({
                    pipelineDetails_call: () => this.gitlab.getLatestPipeline(project.id),
                    project: project
                })
            });
        }

        if (beta_show_scheduled_builds) {
            for (const project of projects) {
                await this.gitlab.getPipelineSchedules(project.id).then(
                    schedules => {
                        schedules.map(schedule => {
                            // console.log(schedule)
                            const pipeline_id = schedule.last_pipeline.id
                            // console.log(promise_call)
                            requests.push({
                                pipelineDetails_call: () => this.gitlab.getPipeline(project.id, pipeline_id),
                                project: project,
                                schedule_description: schedule.description,
                                schedule_url: project.web_url + "/-/pipeline_schedules"
                            })
                        })
                    },
                    _ => {
                        console.log("project " + project.name_with_namespace + " has no schedule builds")
                    }
                )
            }
        }

        const pipelines = await Promise.all(requests.map((request) => {

            const decorate_scheduled_pipelines = (pipeline: Pipeline) => {
                if (request.schedule_description) {
                    pipeline.hint = `scheduled: '${request.schedule_description}'`
                }
                pipeline.hint_url = request.schedule_url || ""
                return Promise.resolve(pipeline)
            };

            return this.avoidTooManyFailedRequests(request.pipelineDetails_call, request.project)
                .then(decorate_scheduled_pipelines)
        }));

        for (let p = 0; p < pipelines.length; p++) {
            const pipeline: Pipeline = pipelines[p]
            const details = pipeline.details;
            if (details?.status == 'failed') {
                if (details.source == "schedule") {
                    pipeline.history = await this.gitlab.getPipelines(details.project_id, details.ref, {source: "schedule"})
                } else {
                    pipeline.history = await this.gitlab.getPipelines(details.project_id, details.ref)
                }
            } else {
                pipeline.history = []
            }
        }

        return pipelines
    }

    private async avoidTooManyFailedRequests(pipelineDetails_call: () => Promise<GitLabPipelineDetails>, project: GitLabProject): Promise<Pipeline> {
        const last_time_if_failed = this.cache_projects_failed.get(project.id)
        if (last_time_if_failed) {
            const wait_milliseconds_before_retry = 600 * 1000
            const now = new Date().getTime()
            const since_diff = now - last_time_if_failed.getTime();
            if (since_diff >= wait_milliseconds_before_retry) {
                console.log("lets retry " + project.id + " " + project.name)
                this.cache_projects_failed.delete(project.id)
            } else {
                // console.log("returns empty for " + project.id)
                return Promise.resolve({} as Pipeline)
            }
        }

        return pipelineDetails_call()
            .then(
                pipeline => this.enrichManualPipelinesWithStatusFromJobs(pipeline, project),
                _error_ => {
                    console.log("failed latest pipeline for " + project.id + "(" + project.name_with_namespace + "). reason " + _error_);
                    this.cache_projects_failed.set(project.id, new Date())
                    return Promise.resolve({} as Pipeline)
                }
            )
    }

    private enrichManualPipelinesWithStatusFromJobs(pipelineDetails: GitLabPipelineDetails, project: GitLabProject): Promise<Pipeline> {
        if (pipelineDetails.status == "manual") {
            const status_counter = {
                canceled: 0,
                failed: 0,
                skipped: 0
            }
            const scopes = Object.keys(status_counter);
            return this.gitlab.getPipelineJobs(project.id, pipelineDetails.id, scopes)
                .then(
                    jobs => {
                        jobs.map(job => {
                            switch (job.status) {
                                case 'failed':
                                    if (!job.allow_failure) {
                                        status_counter.failed++;
                                    }
                                    break
                                case 'skipped':
                                    status_counter.skipped++;
                                    break
                                case 'canceled':
                                    status_counter.canceled++;
                                    break
                                default:
                                    console.warn("unknown status: " + job.status)
                            }
                        })

                        if (status_counter.failed > 0) {
                            pipelineDetails.status = "failed"
                        } else {
                            if (status_counter.skipped > 0) {
                                pipelineDetails.status = "skipped"
                            } else {
                                if (status_counter.canceled > 0) {
                                    pipelineDetails.status = "canceled"
                                } else {
                                    pipelineDetails.status = "success"
                                }
                            }
                        }
                    }
                )
                .then(() => Promise.resolve({
                    details: pipelineDetails,
                    projectName: project.name,
                    namespace: project.namespace.full_path,
                    hint: "manual => status derived from most significant job",
                } as Pipeline))
        }
        return Promise.resolve({
            details: pipelineDetails,
            projectName: project.name,
            namespace: project.namespace.full_path,
        } as Pipeline)
    }

    public async getProjectsPipelines(projects: GitLabProject[],
                                      beta_order_by_failures_first: boolean,
                                      beta_show_only_protected_branches: boolean,
                                      beta_show_scheduled_builds: boolean): Promise<Pipeline[]> {
        const batch = 5
        const pipelines = [] as Pipeline[]

        const project_map = new Map<number, GitLabProject>();
        for (const p of projects) {
            project_map.set(p.id, p)
        }

        for (let i = 0; i < projects.length; i += batch) {
            const projectSlice = projects.slice(i, i + batch);
            const _pipelines = await this.retrieveProjectPipelineDetails(projectSlice, beta_show_only_protected_branches, beta_show_scheduled_builds)
            for (const pipeline of _pipelines.filter(p => "projectName" in p)) {
                if (this.isPipelineZombie(pipeline.details)) {
                    continue
                }
                if (!pipeline.hint) {
                    pipeline.hint = pipeline.details.status
                }
                pipelines.push(pipeline)
            }
        }

        const status_order = beta_order_by_failures_first ?
            (s1: string, s2: string) => {
                return s1.localeCompare(s2)
            } : () => {
                return 0;
            }

        pipelines.sort((e1: Pipeline, e2: Pipeline) => {
            const _status_order = status_order(e1.details.status, e2.details.status)

            const a = e1.namespace + e1.projectName
            const b = e2.namespace + e2.projectName
            const name_order = a.localeCompare(b)

            return _status_order * 10 + name_order
        })

        // deduplicate pipelines
        // because of scheduled builds
        const pipeline_id_map = new Map<number, Pipeline>()
        pipelines.map(p => {
            pipeline_id_map.set(p.details.id, p)
        })
        return Array.from(pipeline_id_map.values())
    }
}

export default GitlabService
